import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Input } from '@/components/ui/input';
import { Check, Eye, EyeOff, Radio, ShieldOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHookTrackWizardStore } from '@/store/hook-track/wizardStore';
import { useTrackingData } from '@/components/tracking/useTrackingData';
import { platformsData, platformSlugToApiName } from '@/components/tracking/platformsData';

/**
 * Etapa 4 do wizard de HookTrack: integração opcional com uma plataforma de
 * rastreamento de conversões. O usuário decide se quer integrar; se quiser,
 * escolhe UMA plataforma e informa UM token, que será aplicado a todas as
 * intros do teste ao finalizar (cada intro vira uma integração de conversão).
 */
export function TrackingIntegrationStep() {
  useLanguage();
  const { data, setTrackingEnabled, setTrackingPlatform, setTrackingToken } = useHookTrackWizardStore();
  const { data: trackingData } = useTrackingData();
  const [showToken, setShowToken] = React.useState(false);

  const tracking = data.trackingIntegration;
  const intros = data.selectedIntros;

  // Resolve o id da plataforma na API a partir do slug do frontend.
  // OBS: id_conversion_plataform é UUID (string) no backend; o subsistema de
  // tracking inteiro tipa como number, então mantemos o mesmo tipo aqui.
  const resolvePlatformId = (slug: string): number | null => {
    const apiName = platformSlugToApiName[slug] || slug;
    const apiPlatform = trackingData?.plataforms?.find((p) => p.name === apiName);
    return apiPlatform ? apiPlatform.id : null;
  };

  // Reconciliação: se a plataforma foi escolhida antes do trackingData carregar,
  // o platformId fica null; ao chegar os dados, resolvemos o id retroativamente.
  React.useEffect(() => {
    if (tracking.platformSlug && trackingData?.plataforms && tracking.platformId === null) {
      const id = resolvePlatformId(tracking.platformSlug);
      if (id !== null) setTrackingPlatform(tracking.platformSlug, id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingData?.plataforms, tracking.platformSlug, tracking.platformId]);

  const handleSelectPlatform = (slug: string) => {
    if (tracking.platformSlug === slug) {
      // Clicar de novo na mesma plataforma desmarca
      setTrackingPlatform(null, null);
      return;
    }
    setTrackingPlatform(slug, resolvePlatformId(slug));
  };

  const selectedPlatformData = tracking.platformSlug
    ? platformsData.find((p) => p.id === tracking.platformSlug)
    : null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[960px] mx-auto w-full">
      {/* Escolha: integrar ou não */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8">
        <button
          type="button"
          onClick={() => setTrackingEnabled(true)}
          className={cn(
            'relative flex items-start gap-3 rounded-2xl border p-4 sm:p-5 text-left transition-all',
            tracking.enabled
              ? 'border-2 border-[#2563EB] bg-[#F5F8FF] shadow-[0_2px_8px_rgba(37,99,235,0.12)]'
              : 'border border-[#E7EAF2] bg-white hover:border-[#C7D2FE]'
          )}
        >
          <div className={cn('mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full', tracking.enabled ? 'bg-[#2563EB] text-white' : 'bg-[#EEF2FF] text-[#2563EB]')}>
            <Radio className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="text-[15px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Integrar rastreamento")}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-[#6B7280]">
              {translateInterfaceText("Conecte uma plataforma de conversão para ver vendas e receita por intro nas métricas do teste.")}</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setTrackingEnabled(false)}
          className={cn(
            'relative flex items-start gap-3 rounded-2xl border p-4 sm:p-5 text-left transition-all',
            !tracking.enabled
              ? 'border-2 border-[#2563EB] bg-[#F5F8FF] shadow-[0_2px_8px_rgba(37,99,235,0.12)]'
              : 'border border-[#E7EAF2] bg-white hover:border-[#C7D2FE]'
          )}
        >
          <div className={cn('mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full', !tracking.enabled ? 'bg-[#2563EB] text-white' : 'bg-[#F3F4F6] text-[#6B7280]')}>
            <ShieldOff className="h-[18px] w-[18px]" />
          </div>
          <div>
            <p className="text-[15px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Continuar sem rastreamento")}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-[#6B7280]">
              {translateInterfaceText("O teste roda normalmente, mas sem as métricas de conversão das plataformas de pagamento.")}</p>
          </div>
        </button>
      </div>

      {/* Configuração da integração (só aparece quando ativada) */}
      {tracking.enabled && (
        <div className="space-y-8">
          {/* Seleção de plataforma */}
          <div>
            <h3 className="mb-1 text-[15px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Selecione a plataforma")}</h3>
            <p className="mb-4 text-[13px] text-[#6B7280]">{translateInterfaceText("Escolha onde suas conversões são processadas.")}</p>
            <div className="grid grid-cols-2 gap-[14px] sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 sm:gap-4 md:gap-5">
              {platformsData.map((platform) => {
                const isSelected = tracking.platformSlug === platform.id;
                return (
                  <div
                    key={platform.id}
                    onClick={() => handleSelectPlatform(platform.id)}
                    className={cn(
                      'relative flex h-[70px] w-full cursor-pointer items-center justify-center rounded-2xl bg-white transition-all duration-200 ease-in-out sm:h-[90px] md:h-[112px]',
                      isSelected
                        ? 'border-2 border-[#2563EB] shadow-[0_2px_8px_rgba(37,99,235,0.15)]'
                        : 'border border-[#E7EAF2] hover:border-[#C7D2FE]'
                    )}
                  >
                    {isSelected && (
                      <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#2563EB] text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <div className="flex h-full w-full items-center justify-center px-3 sm:px-4">
                      <img
                        src={platform.logo}
                        alt={translateInterfaceText("{value0} logo", { value0: (platform.name) })}
                        className="max-h-[36px] w-auto max-w-full object-contain opacity-95 sm:max-h-[44px] md:max-h-[50px]"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Token + intros que serão rastreadas */}
          {tracking.platformSlug && (
            <div>
              <h3 className="mb-1 text-[15px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Token da plataforma")}</h3>
              <p className="mb-4 text-[13px] text-[#6B7280]">
                {translateInterfaceText("Informe o token de")}{" "}{selectedPlatformData?.name}{translateInterfaceText(". Ele será usado para todas as intros do teste.")}</p>

              <div className="relative mb-6 max-w-[420px]">
                <Input
                  placeholder={translateInterfaceText("Token de {value0}", { value0: (selectedPlatformData?.name || 'plataforma') })}
                  type={showToken ? 'text' : 'password'}
                  value={tracking.platformToken}
                  onChange={(e) => setTrackingToken(e.target.value)}
                  className="h-11 rounded-[10px] pr-10 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground focus:outline-none"
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Intros que receberão a integração */}
              <div className="overflow-hidden rounded-[14px] border border-[#E7EAF2]">
                <div className="flex items-center gap-2 border-b border-[#E7EAF2] bg-[#FAFBFF] px-4 py-3">
                  {selectedPlatformData && (
                    <img
                      src={selectedPlatformData.logo}
                      alt={selectedPlatformData.name}
                      className="h-5 w-auto max-w-[90px] object-contain"
                    />
                  )}
                  <span className="text-[13px] font-semibold text-[#5A6475]">
                    {intros.length} {intros.length === 1 ? translateInterfaceText("intro será rastreada") : translateInterfaceText("intros serão rastreadas")}
                  </span>
                </div>
                <ul className="divide-y divide-[#EDF1FF]">
                  {intros.map((intro, index) => (
                    <li key={intro.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="h-9 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                        {intro.thumbnail && (
                          <img src={intro.thumbnail} alt={intro.name} className="h-full w-full object-cover" />
                        )}
                      </div>
                      <span className="truncate text-[14px] font-medium text-[#2C3345]">
                        {intro.name || translateInterfaceText("Intro #{value0}", { value0: (index + 1) })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
